import { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown, LucideIcon } from "lucide-react";
import {
    ShieldCheck,
    FileSearch,
    ScanText,
    Database,
    Workflow,
    BarChart3,
    Lock,
    Rocket,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface Feature {
    icon: LucideIcon;
    title: string;
    desc: string;
}

/* ---------------- Region / Language ---------------- */

type LangCode = "en" | "es" | "it";
function RegionLanguageMenu({
    value,
    onChange,
}: {
    value: LangCode;
    onChange: (lang: LangCode) => void;
}) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const options: Array<{ country: string; label: string; code: LangCode }> = [
        { country: "United States", label: "English", code: "en" },
        { country: "United States", label: "Spanish", code: "es" },
        { country: "United States", label: "Italian", code: "it" },
    ];

    const current =
        options.find((o) => o.code === value) ?? {
            country: "United States",
            label: "English",
            code: "en",
        };

    useEffect(() => {
        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDocDown);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocDown);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    return (
        <div className="relative">
            <button
                ref={btnRef}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {current.label}
                <ChevronDown
                    size={14}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <div
                    ref={panelRef}
                    className="absolute right-0 mt-2 w-56 rounded-md border bg-white shadow-xl ring-1 ring-black/5 z-[10000]"
                >
                    {options.map((o) => (
                        <button
                            key={o.code}
                            onClick={() => {
                                onChange(o.code);
                                setOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${value === o.code ? "bg-slate-100 font-medium" : ""
                                }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- Menu Model ---------------- */
const MENU = [
    {
        label: "Features",
        children: [
            { label: "Import PDFs", to: "/solutions/extracta/import" },
            { label: "Extract Data", to: "/solutions/extracta/extract" },
            { label: "Validate & Review", to: "/solutions/extracta/review" },
            { label: "Export & Integrate", to: "/solutions/extracta/export" },
            { label: "Analytics", to: "/solutions/extracta/analytics" },
        ],
    },
    {
        label: "Services",
        children: [
            { label: "Template & Mapping Setup", to: "/services/extracta-setup" },
            { label: "Workflow Automation", to: "/services/automation" },
        ],
    },
    {
        label: "Resources",
        children: [
            { label: "Help Center", to: "/resources/help" },
            { label: "Resource Library", to: "/resources/library" },
            { label: "Training", to: "/resources/training" },
            { label: "FAQ", to: "/resources/faq" },
        ],
    },
    {
        label: "About",
        children: [
            { label: "Why SecureID", to: "/about/why-secureid" },
            { label: "Contact Us", to: "/solutions/extracta/support" },
        ],
    },
];

function ExtractaPortal() {
    return (
        <section className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
                <Link
                    to="/extracta/login"
                    className="w-full sm:w-auto text-center bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-indigo-700 transition"
                >
                    🧩 Extracta Login
                </Link>
                <Link
                    to="/contact"
                    className="w-full sm:w-auto text-center bg-emerald-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-emerald-700 transition"
                >
                    🧑‍💼 Talk to Sales
                </Link>
            </div>
        </section>
    );
}

export default function Extracta() {
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { i18n } = useTranslation();

    useEffect(() => {
        const closeMenus = () => setOpenMenu(null);
        document.addEventListener("click", closeMenus);
        return () => document.removeEventListener("click", closeMenus);
    }, []);

    const toggleMobile = () => setMobileOpen(!mobileOpen);

    const features: Feature[] = [
        {
            icon: FileSearch,
            title: "Smart Document Intake",
            desc: "Upload PDFs, email attachments, or batches—ready for classification and extraction.",
        },
        {
            icon: ScanText,
            title: "AI-Assisted Extraction",
            desc: "Capture key fields with guided extraction that accelerates your workflow.",
        },
        {
            icon: Workflow,
            title: "Validation & Review",
            desc: "Human-in-the-loop review with confidence cues, highlighting, and audit trails.",
        },
        {
            icon: Database,
            title: "Export & Integrate",
            desc: "Export to CSV/JSON or connect to your systems via webhooks and APIs.",
        },
        {
            icon: BarChart3,
            title: "Operational Analytics",
            desc: "Track throughput, accuracy, exceptions, and turnaround time by team or client.",
        },
        {
            icon: Lock,
            title: "Security by Design",
            desc: "Role-based access, encryption, and controls aligned with enterprise expectations.",
        },
    ];

    return (
        <main className="bg-white text-gray-900">
            {/* ---------------- Navbar ---------------- */}
            <header className="bg-white shadow-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    {/* Logo */}
                    <div className="flex items-center space-x-3">
                        <img
                            src="/Images/Extracta.png"
                            alt="Extracta Logo"
                            className="w-[42px] h-auto rounded-lg shadow border border-gray-200"
                        />
                        <span className="text-xl font-bold text-gray-900">
                            SecureID <span className="text-indigo-600">Extracta</span>
                        </span>
                    </div>

                    {/* Desktop Nav */}
                    <nav
                        className="hidden md:flex items-center space-x-8 font-medium text-gray-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* UtilityBar */}
                        <div className="flex items-center gap-4 border-l pl-6 ml-4 border-gray-200">
                            <Link to="/extracta/login" className="hover:text-indigo-600">
                                Extracta Login
                            </Link>
                            <Link to="/solutions/extracta/support" className="hover:text-indigo-600">
                                Customer Support
                            </Link>
                            <Link to="/contact" className="hover:text-indigo-600">
                                Schedule a Demo
                            </Link>
                        </div>

                        {/* Main sections */}
                        {["features", "services", "resources", "about"].map((menu) => (
                            <div key={menu} className="relative">
                                <button
                                    onClick={() => setOpenMenu(openMenu === menu ? null : menu)}
                                    className="flex items-center gap-1 hover:text-indigo-600 capitalize"
                                >
                                    {menu}
                                    <svg
                                        className="w-4 h-4 ml-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </button>

                                {openMenu === menu && (
                                    <div className="absolute left-0 mt-2 bg-white border border-gray-100 rounded-lg shadow-lg w-56 z-50">
                                        <ul className="py-2 text-sm text-gray-700">
                                            {menu === "features" && (
                                                <>
                                                    <li>
                                                        <Link
                                                            to="/solutions/extracta/import"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Import PDFs
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/extracta/extract"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Extract Data
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/extracta/review"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Validate & Review
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/extracta/export"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Export & Integrate
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/extracta/analytics"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Analytics
                                                        </Link>
                                                    </li>
                                                </>
                                            )}
                                            {menu === "services" && (
                                                <>
                                                    <li>
                                                        <Link
                                                            to="/services/extracta-setup"
                                                            className="block px-4 py-2 hover:bg-gray-50"
                                                        >
                                                            Template & Mapping Setup
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/services/automation"
                                                            className="block px-4 py-2 hover:bg-gray-50"
                                                        >
                                                            Workflow Automation
                                                        </Link>
                                                    </li>
                                                </>
                                            )}
                                            {menu === "resources" && (
                                                <>
                                                    <li>
                                                        <Link to="/resources/help" className="block px-4 py-2 hover:bg-gray-50">
                                                            Help Center
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/resources/library"
                                                            className="block px-4 py-2 hover:bg-gray-50"
                                                        >
                                                            Resource Library
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/resources/training"
                                                            className="block px-4 py-2 hover:bg-gray-50"
                                                        >
                                                            Training
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link to="/resources/faq" className="block px-4 py-2 hover:bg-gray-50">
                                                            FAQ
                                                        </Link>
                                                    </li>
                                                </>
                                            )}
                                            {menu === "about" && (
                                                <>
                                                    <li>
                                                        <Link
                                                            to="/about/why-secureid"
                                                            className="block px-4 py-2 hover:bg-gray-50"
                                                        >
                                                            Why SecureID
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/extracta/support"
                                                            className="block px-4 py-2 hover:bg-gray-50"
                                                        >
                                                            Contact Us
                                                        </Link>
                                                    </li>
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </nav>

                    {/* Mobile toggle */}
                    <button onClick={toggleMobile} className="md:hidden text-gray-700">
                        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Optional portal strip */}
                <ExtractaPortal />

                {/* MOBILE MENU */}
                {mobileOpen && (
                    <div className="md:hidden bg-white border-t border-gray-200 shadow-inner max-h-[85vh] overflow-y-auto px-6 py-4 space-y-4">
                        {/* Main menu items */}
                        {MENU.map((item) => (
                            <div key={item.label}>
                                <div className="text-[15px] font-semibold text-gray-900 mb-2">
                                    {item.label}
                                </div>
                                <ul className="pl-3 mt-2 space-y-1 bg-indigo-50 rounded-md border border-indigo-100">
                                    {item.children.map((child) => (
                                        <li key={child.label}>
                                            <NavLink
                                                to={child.to}
                                                onClick={() => setMobileOpen(false)}
                                                className={({ isActive }) =>
                                                    [
                                                        "block w-full rounded-md px-3 py-2 text-[15px] transition font-medium !text-indigo-900",
                                                        isActive
                                                            ? "!bg-indigo-100 !text-indigo-800 font-semibold"
                                                            : "hover:!bg-indigo-100 hover:!text-indigo-700",
                                                    ].join(" ")
                                                }
                                            >
                                                {child.label}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        {/* UtilityBar Options */}
                        <div className="pt-4 border-t border-gray-200">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Utility</p>
                            <div className="flex flex-col space-y-2 text-indigo-700 font-medium">
                                <Link to="/extracta/login" onClick={() => setMobileOpen(false)}>
                                    Extracta Login
                                </Link>
                                <Link to="/contact" onClick={() => setMobileOpen(false)}>
                                    Schedule a Demo
                                </Link>
                                <Link
                                    to="/solutions/extracta/support"
                                    onClick={() => setMobileOpen(false)}
                                >
                                    Customer Support
                                </Link>
                            </div>
                        </div>

                        {/* 🌎 Language Selector */}
                        <div className="pt-3 border-t mt-2 space-y-3 !text-indigo-900">
                            <div className="px-2">
                                <RegionLanguageMenu
                                    value={(i18n.language as LangCode) || "en"}
                                    onChange={(lng) => {
                                        i18n.changeLanguage(lng);
                                        setMobileOpen(false);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* ---------------- Hero ---------------- */}
            <section className="bg-gradient-to-br from-indigo-700 via-indigo-800 to-indigo-900 text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-indigo-600/30 border border-indigo-400/40 text-indigo-100 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                            <span className="inline-block w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                            AI-Assisted Extraction
                        </div>

                        <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
                            Turn PDFs into <span className="text-indigo-300">Structured Data</span>
                        </h1>
                        <p className="mt-6 text-lg text-indigo-100">
                            Extracta helps teams capture key fields from documents, validate results, and export clean data—fast.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                📅 Schedule a Demo
                            </Link>

                            <Link
                                to="/pricing"
                                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                View Pricing
                            </Link>

                            <Link
                                to="/solutions/extracta/import"
                                className="flex items-center gap-2 bg-indigo-700 hover:bg-indigo-600 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                <Rocket className="w-4 h-4" />
                                Get Started
                            </Link>
                        </div>
                    </div>

                    {/* RIGHT: mockup */}
                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="relative flex justify-center items-center"
                    >
                        <motion.div
                            animate={{ y: [0, -6, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-tr from-emerald-400 via-indigo-500 to-purple-600 p-1 rounded-[2.5rem] shadow-2xl">
                                <div className="relative rounded-[2.2rem] bg-white/5 backdrop-blur overflow-hidden border border-white/10">
                                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-3 h-3 bg-white/30 rounded-full" />
                                    <div className="p-4">
                                        <div className="rounded-2xl overflow-hidden shadow-xl bg-gray-100">
                                            <img
                                                src="/Images/extracta-dashboard.jpg"
                                                alt="Extracta Dashboard"
                                                className="w-full h-auto object-cover"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* ---------------- Extracta Assist ---------------- */}
            <section className="bg-indigo-50/50 py-24">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                    <div className="order-2 md:order-1">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
                            Introducing <span className="text-indigo-700">Extracta Assist</span>
                        </h2>
                        <p className="mt-4 text-gray-700 text-lg">
                            A guided assistant that accelerates mapping, extraction, and validation—keeping your team in control.
                        </p>
                        <ul className="mt-6 space-y-2 text-gray-700">
                            <li>• Field suggestions and confidence cues.</li>
                            <li>• Review queues for exceptions and low-confidence values.</li>
                            <li>• Optional by design—use it when you want.</li>
                            <li>• Private by default—we don’t train on your data.</li>
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link
                                to="/resources/training"
                                className="bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-indigo-700 shadow"
                            >
                                Learn about Assist
                            </Link>

                            <Link
                                to="/pricing"
                                className="bg-indigo-100 text-indigo-700 px-6 py-3 rounded-full font-semibold border border-indigo-200 hover:bg-indigo-200 shadow-sm"
                            >
                                View Pricing
                            </Link>

                            <Link
                                to="/solutions/extracta/support"
                                className="bg-white text-indigo-700 px-6 py-3 rounded-full font-semibold border border-indigo-200 hover:bg-indigo-50 shadow-sm"
                            >
                                Talk to an expert
                            </Link>
                        </div>
                    </div>

                    <div className="order-1 md:order-2">
                        <div className="rounded-2xl overflow-hidden shadow-2xl bg-white border border-indigo-100">
                            <img src="/Images/extracta-bg.jpg" alt="Extracta Assist" className="w-full h-auto" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- Feature Cards ---------------- */}
            <section className="bg-gray-50 py-20 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-14">
                        Extracta Helps You…
                    </h2>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
                        {features.map((f) => (
                            <div
                                key={f.title}
                                className="bg-white rounded-2xl shadow-sm hover:shadow-md p-8 border border-gray-100 transition"
                            >
                                <div className="flex justify-center mb-4">
                                    <div className="p-3 rounded-full bg-indigo-50">
                                        <f.icon className="w-10 h-10 text-indigo-600" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{f.title}</h3>
                                <p className="text-gray-600 mb-6">{f.desc}</p>
                                <Link
                                    to="/solutions/extracta/extract"
                                    className="inline-block bg-indigo-600 text-white px-6 py-2 rounded-full hover:bg-indigo-700 transition"
                                >
                                    Learn More
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- Security ---------------- */}
            <section className="bg-gray-50 py-20 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ x: -100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
                            Secure Extraction for Sensitive Documents
                        </h2>
                        <ul className="space-y-3 text-gray-700">
                            <li>• Role-based access controls and audit-ready logs.</li>
                            <li>• Encryption in transit and at rest.</li>
                            <li>• Controlled exports and least-privilege workflows.</li>
                            <li>• Enterprise-aligned security posture and policies.</li>
                        </ul>
                        <div className="mt-6 flex gap-4">
                            <Link
                                to="/security"
                                className="bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-indigo-700"
                            >
                                Learn More
                            </Link>
                            <Link
                                to="/compliance"
                                className="bg-indigo-100 text-indigo-700 px-6 py-3 rounded-full font-semibold hover:bg-indigo-200"
                            >
                                View Compliance
                            </Link>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ x: 100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="flex justify-center"
                    >
                        <img
                            src="/Images/document-extraction.jpg"
                            alt="Extracta Security"
                            className="rounded-2xl shadow-xl border border-gray-200"
                        />
                    </motion.div>
                </div>
            </section>

            {/* ---------------- Integrations ---------------- */}
            <section className="bg-white py-24">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ x: -100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <img
                            src="/Images/contract-bg.jpg"
                            alt="Integrations"
                            className="rounded-2xl shadow-2xl border border-gray-200"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ x: 100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
                            Connect Extracta to Your Workflow
                        </h2>
                        <ul className="space-y-3 text-gray-700">
                            <li>• Export to CSV, JSON, or custom payloads.</li>
                            <li>• Trigger automations with webhooks and API calls.</li>
                            <li>• Route documents by type, client, or project.</li>
                            <li>• Optional hands-on integration support.</li>
                        </ul>
                        <div className="mt-6 flex gap-4">
                            <Link
                                to="/integrations"
                                className="bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-indigo-700"
                            >
                                Explore Integrations
                            </Link>
                            <Link
                                to="/contact"
                                className="bg-indigo-100 text-indigo-700 px-6 py-3 rounded-full font-semibold hover:bg-indigo-200"
                            >
                                Talk to Support
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ---------------- Ideal For ---------------- */}
            <section className="bg-white py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">
                        Extracta is Ideal For
                    </h2>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            ["Invoices & Receipts", "icons/compliance.svg"],
                            ["Contracts & Agreements", "icons/pro-cert.svg"],
                            ["HR & Onboarding Packets", "icons/admissions.svg"],
                            ["Insurance Claims", "icons/health.svg"],
                            ["Government Forms", "icons/government.svg"],
                            ["Compliance & Audits", "icons/compliance.svg"],
                            ["Financial Documents", "icons/cert-badge.svg"],
                            ["Vendor & Procurement", "icons/preemployment.svg"],
                        ].map(([label, icon]) => (
                            <div
                                key={label as string}
                                className="p-6 bg-gray-50 rounded-xl border hover:shadow transition flex items-center gap-4"
                            >
                                <img src={`/${icon}`} alt="" className="h-8 w-8" />
                                <span className="font-semibold text-gray-900">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- Testimonials ---------------- */}
            <section className="bg-gray-50 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">
                        Why Teams Choose Extracta
                    </h2>

                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {[
                            {
                                quote:
                                    "We reduced manual data entry dramatically. Reviews are fast and the audit trail gives us confidence.",
                                name: "Operations Manager",
                                org: "Regional Services Group",
                            },
                            {
                                quote:
                                    "Mapping our documents was straightforward and the validation workflow kept quality high.",
                                name: "Process Lead",
                                org: "Healthcare Provider",
                            },
                            {
                                quote:
                                    "Exporting clean data into our systems made billing and reporting much faster across the team.",
                                name: "Finance Director",
                                org: "Multi-Location Business",
                            },
                        ].map((t) => (
                            <figure key={t.quote} className="p-6 bg-white rounded-2xl shadow-sm border">
                                <blockquote className="text-gray-700">“{t.quote}”</blockquote>
                                <figcaption className="mt-4 text-sm text-gray-500">
                                    <span className="font-semibold text-gray-900">{t.name}</span>, {t.org}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- Two-Path CTA ---------------- */}
            <section className="bg-indigo-700 text-white py-20">
                <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
                    <div className="bg-indigo-600/40 rounded-2xl p-8 border border-indigo-500">
                        <h3 className="text-2xl font-bold">I want to automate extraction in-house</h3>
                        <p className="mt-3 text-indigo-100">
                            Use Extracta to intake PDFs, extract key fields, validate results, and export structured data.
                        </p>
                        <Link
                            to="/solutions/extracta/extract"
                            className="inline-block mt-6 border border-white text-white font-semibold px-5 py-2 rounded-full hover:bg-white hover:text-indigo-700 transition"
                        >
                            Learn More
                        </Link>
                    </div>

                    <div className="bg-indigo-600/40 rounded-2xl p-8 border border-indigo-500">
                        <h3 className="text-2xl font-bold">I want SecureID to configure it for my documents</h3>
                        <p className="mt-3 text-indigo-100">
                            Partner with our team to map templates, build validation rules, and integrate with your systems.
                        </p>
                        <Link
                            to="/services/extracta-setup"
                            className="inline-block mt-6 border border-white text-white font-semibold px-5 py-2 rounded-full hover:bg-white hover:text-indigo-700 transition"
                        >
                            Learn More
                        </Link>
                    </div>
                </div>
            </section>

            {/* ---------------- Footer ---------------- */}
            <footer className="bg-gray-900 text-gray-300 py-16">
                <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 md:grid-cols-4 gap-10">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-3">SecureID Technologies</h3>
                        <p className="text-sm mb-4">
                            Extracta — AI-assisted document extraction for modern teams.
                        </p>
                    </div>

                    <div>
                        <h4 className="font-semibold text-white mb-3">Company</h4>
                        <ul className="space-y-1 text-sm">
                            <li>
                                <Link to="/about">About SecureID</Link>
                            </li>
                            <li>
                                <Link to="/careers">Careers</Link>
                            </li>
                            <li>
                                <Link to="/contact">Contact Us</Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-white mb-3">Resources</h4>
                        <ul className="space-y-1 text-sm">
                            <li>
                                <Link to="/help">Help Center</Link>
                            </li>
                            <li>
                                <Link to="/docs">Documentation</Link>
                            </li>
                            <li>
                                <Link to="/privacy">Privacy & Security</Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-white mb-3">Products</h4>
                        <ul className="space-y-1 text-sm">
                            <li>
                                <Link to="/solutions/examspro">ExamsPro</Link>
                            </li>
                            <li>
                                <Link to="/solutions/extracta">Extracta</Link>
                            </li>
                            <li>
                                <Link to="/solutions/dactylscan">DactylScan</Link>
                            </li>
                            <li>
                                <Link to="/solutions/background">Background Checks</Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-gray-700 mt-10 pt-6 text-center text-sm">
                    <p>
                        © 2025 SecureID Technologies LLC — All Rights Reserved.{" "}
                        <Link to="/legal" className="text-indigo-400 hover:underline mx-1">
                            Legal Stuff
                        </Link>{" "}
                        |{" "}
                        <Link to="/privacy" className="text-indigo-400 hover:underline mx-1">
                            Privacy Policy
                        </Link>
                    </p>
                </div>
            </footer>
        </main>
    );
}
